import { getErrorMessage } from '@/api/errors'
import { MarkdownMonacoEditor } from '@/components/context-sources/MarkdownMonacoEditor'
import { MarkdownEditor } from '@/components/MarkdownEditor'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useContextSourceContent, useUpdateContextSourceContent } from '@/hooks'
import type { ContextSourceListItem } from '@/types'
import { Check, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

// tiptap renders every node into the DOM with no virtualization, so a multi-megabyte file freezes
// the tab. Past this size the content goes to the virtualized editor instead. This is about size
// alone — formatting never sends content there.
const MAX_VISUALLY_EDITABLE_BYTES = 256 * 1024

interface EditContextSourceDialogProps {
  projectId: string
  source: ContextSourceListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditContextSourceDialog({ projectId, source, open, onOpenChange }: EditContextSourceDialogProps) {
  const [displaySource, setDisplaySource] = useState<ContextSourceListItem | null>(source)

  if (source && source !== displaySource) {
    setDisplaySource(source)
  }

  const { data, isLoading, isError, error, refetch } = useContextSourceContent(projectId, open && displaySource ? displaySource.id : null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">Edit {displaySource?.name}</DialogTitle>
        </DialogHeader>

        {!displaySource ? null : isError ? (
          <div className="flex flex-col items-center justify-center gap-3 flex-1 text-muted-foreground" role="alert">
            {getErrorMessage(error) ?? 'Failed to load content.'}
            <Button variant="outline" onClick={() => refetch()}>Retry</Button>
          </div>
        ) : (isLoading || !data) ? (
          <div className="flex items-center justify-center flex-1" role="status" aria-live="polite">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <EditContextSourceForm
            projectId={projectId}
            sourceId={displaySource.id}
            content={data.content}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

interface EditContextSourceFormProps {
  projectId: string
  sourceId: string
  content: string
  onOpenChange: (open: boolean) => void
}

interface EditContextSourceFormValues {
  content: string
}

function EditContextSourceForm({ projectId, sourceId, content, onOpenChange }: EditContextSourceFormProps) {
  const updateContent = useUpdateContextSourceContent(projectId)

  const { control, handleSubmit, formState: { isDirty } } = useForm<EditContextSourceFormValues>({
    defaultValues: { content },
  })

  const onSubmit = async (data: EditContextSourceFormValues) => {
    if (!isDirty) {
      onOpenChange(false)
      return
    }

    try {
      await updateContent.mutateAsync({ id: sourceId, content: data.content })
      toast.success('Context source updated')
      onOpenChange(false)
    } catch (err) {
      toast.error(getErrorMessage(err) ?? 'Failed to update context source')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 max-w-6xl mx-auto py-8 px-6 w-full gap-3">
        <Controller
          name="content"
          control={control}
          render={({ field }) => (
            (field.value?.length ?? 0) > MAX_VISUALLY_EDITABLE_BYTES ? (
              <div className="flex-1 min-h-0 overflow-hidden rounded-md border border-input">
                <MarkdownMonacoEditor
                  dataTestId="edit-context-source-content-input"
                  value={field.value}
                  onChange={field.onChange}
                />
              </div>
            ) : (
              <MarkdownEditor
                dataTestId="edit-context-source-content-input"
                value={field.value}
                onChange={field.onChange}
                fillHeight
                preserveSourceFidelity
              />
            )
          )}
        />
      </div>
      <DialogFooter className="px-8 py-4 border-t shrink-0 bg-background">
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button type="submit" disabled={updateContent.isPending}>
          <Check className="h-5 w-5" />
          {updateContent.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </form>
  )
}
